import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService, { type PmvFleetTodayRow } from '@/services/pmv.service';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

const { Text } = Typography;

const statusColor: Record<string, string> = {
  DRAFT: 'default',
  SUBMITTED: 'processing',
  CERTIFIED: 'success',
  RETURNED: 'warning',
  VOID: 'error',
  MISSING: 'error',
};

function humanizeLogState(
  logState: string | null | undefined,
  logStatus: string | null | undefined
): string {
  const state = String(logState || '').toUpperCase();
  if (state === 'MISSING' || (!logStatus && state !== 'OK')) return 'Daily log missing';
  if (logStatus) return logStatus;
  return state || '—';
}

const PmvDailyLogRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const summaryQ = useQuery({
    queryKey: ['pmv-operations-daily', today],
    queryFn: () => pmvService.getOperationsDailySummary(today),
  });

  const logsQ = useQuery({
    queryKey: ['pmv-daily-logs', today, statusFilter],
    queryFn: () =>
      pmvService.listDailyLogs({
        operating_date: today,
        pageSize: 100,
        ...(statusFilter ? { status: statusFilter } : {}),
      }),
  });

  const summary = summaryQ.data;
  const fleet = (summary?.fleet ?? []) as PmvFleetTodayRow[];
  const useFleetView = fleet.length > 0;

  const fleetRows = useMemo(() => {
    if (!statusFilter) return fleet;
    const f = statusFilter.toUpperCase();
    return fleet.filter((r) => {
      if (f === 'MISSING') return r.logState === 'MISSING' || !r.logId;
      return String(r.logStatus || '').toUpperCase() === f;
    });
  }, [fleet, statusFilter]);

  const logRows = useMemo(() => logsQ.data?.data ?? [], [logsQ.data?.data]);

  const expected =
    summary?.deployedUnits ?? (summary ? summary.logsCertified + summary.missingLogs : 0);
  const certified = summary?.logsCertified ?? 0;
  const missing = summary?.missingLogs ?? 0;
  const empty =
    !summaryQ.isLoading &&
    !logsQ.isLoading &&
    (useFleetView ? fleetRows.length === 0 : logRows.length === 0);

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Field Operations"
          documentNumber={today}
          subtitle={
            <Text type="secondary">Daily Equipment Logs — individual operational truth</Text>
          }
          statusTag={<Tag color="blue">Operating date {today}</Tag>}
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/control-tower')}>Command Center</Button>
              <Button type="primary" onClick={() => navigate('/asset/pmv/daily-log-sheets')}>
                Daily Log Sheets
              </Button>
              <Button onClick={() => navigate('/asset/pmv/daily-log-sheets/exceptions')}>
                Field exceptions
              </Button>
              <Button onClick={() => navigate('/asset/pmv/operations')}>Deployment</Button>
            </Space>
          }
          extra={
            <Select
              allowClear
              placeholder="Filter status"
              style={{ width: 160 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                ...['DRAFT', 'SUBMITTED', 'RETURNED', 'CERTIFIED', 'VOID'].map((v) => ({
                  value: v,
                  label: v,
                })),
                ...(useFleetView ? [{ value: 'MISSING', label: 'Daily log missing' }] : []),
              ]}
            />
          }
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {missing > 0 ? (
          <Alert
            type="warning"
            showIcon
            message={`${missing} deployed unit${missing === 1 ? '' : 's'} missing a certified daily log today`}
            action={
              <Button size="small" onClick={() => setStatusFilter('MISSING')}>
                Show missing
              </Button>
            }
          />
        ) : null}

        <Card size="small">
          <Row gutter={[16, 16]}>
            <Col xs={8}>
              <Statistic title="Expected" value={expected} loading={summaryQ.isLoading} />
            </Col>
            <Col xs={8}>
              <Statistic title="Certified" value={certified} loading={summaryQ.isLoading} />
            </Col>
            <Col xs={8}>
              <Statistic
                title="Missing"
                value={missing}
                loading={summaryQ.isLoading}
                valueStyle={missing ? { color: '#cf1322' } : undefined}
              />
            </Col>
          </Row>
        </Card>

        {empty ? (
          <Empty description="No daily log rows for this filter">
            <Button type="primary" onClick={() => navigate('/asset/pmv/daily-log-sheets')}>
              Open bulk sheets
            </Button>
          </Empty>
        ) : useFleetView ? (
          <Table
            rowKey={(r) => r.allocationId || r.profileId}
            size="small"
            loading={summaryQ.isLoading}
            dataSource={fleetRows}
            pagination={{ pageSize: 50 }}
            scroll={{ x: 900 }}
            tableLayout="fixed"
            columns={[
              {
                title: 'Unit',
                width: 260,
                ellipsis: true,
                render: (_: unknown, r: PmvFleetTodayRow) => (
                  <div style={hireTableCellEllipsis}>
                    <Button
                      type="link"
                      style={hireTableLinkBtnStyle}
                      onClick={() => navigate(`/asset/pmv/${r.profileId}`)}
                    >
                      {r.assetCode
                        ? `${r.assetCode}${r.assetName ? ` — ${r.assetName}` : ''}`
                        : r.profileId}
                    </Button>
                  </div>
                ),
              },
              {
                title: 'Allocation',
                dataIndex: 'allocationStatus',
                width: 140,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: 'Log status',
                width: 160,
                render: (_: unknown, r: PmvFleetTodayRow) => {
                  const label = humanizeLogState(r.logState, r.logStatus);
                  const color =
                    r.logState === 'MISSING' || !r.logId
                      ? statusColor.MISSING
                      : statusColor[String(r.logStatus || '')] ?? 'default';
                  return <Tag color={color}>{label}</Tag>;
                },
              },
              {
                title: 'Actions',
                width: 180,
                render: (_: unknown, r: PmvFleetTodayRow) => {
                  if (r.logId) {
                    return (
                      <Button
                        type="link"
                        onClick={() => navigate(`/asset/pmv/daily-logs/${r.logId}`)}
                      >
                        Open log
                      </Button>
                    );
                  }
                  if (r.allocationId) {
                    return (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => navigate(`/asset/pmv/allocations/${r.allocationId}`)}
                      >
                        Create daily log
                      </Button>
                    );
                  }
                  return (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Open allocation workspace
                    </Text>
                  );
                },
              },
            ]}
          />
        ) : (
          <Table
            rowKey="id"
            size="small"
            loading={logsQ.isLoading}
            dataSource={logRows}
            pagination={{ pageSize: 50 }}
            scroll={{ x: 800 }}
            tableLayout="fixed"
            onRow={(r) => ({
              onClick: () => navigate(`/asset/pmv/daily-logs/${r.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: 'Log #',
                dataIndex: 'log_number',
                width: 160,
                ellipsis: true,
                render: (v: string) => <div style={hireTableCellEllipsis}>{v}</div>,
              },
              { title: 'Date', dataIndex: 'operating_date', width: 110 },
              { title: 'Shift', dataIndex: 'shift_code', width: 90 },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 110,
                render: (v: string) => <Tag color={statusColor[v] ?? 'default'}>{v}</Tag>,
              },
              {
                title: 'Working',
                dataIndex: 'working_minutes',
                width: 90,
                render: (v: number) => `${(v / 60).toFixed(1)} h`,
              },
              {
                title: '',
                width: 80,
                render: (_, r) => (
                  <Button
                    type="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/asset/pmv/daily-logs/${r.id}`);
                    }}
                  >
                    Open
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Space>
    </WorkspaceLayout>
  );
};

export default PmvDailyLogRegisterPage;
